import { SchoolZone, SchoolZoneSmall } from '@ice-web-app/assets';
import { ContentZone, DsIcon, DsTypography, SchoolMenu } from '@ice-web-app/designSystem';
import { TIPS, ZONES_BASE_PATH } from '@ice-web-app/shared-constants';
import { usePopover, useTranslation } from '@ice-web-app/shared-hooks';
import { useCurrentUserStore, useZoneStore } from '@ice-web-app/shared-store';
import { getCurrentZoneBasePath } from '@ice-web-app/shared-utils';
import { Box, Stack } from '@mui/material';
import { MouseEventHandler, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const ZoneItem = ({
  disabled,
  isActive,
  title,
  icon,
  subTitle,
  onClick,
}: {
  disabled: boolean;
  isActive: boolean;
  title: string;
  icon: React.ReactNode;
  subTitle?: string;
  onClick?: () => void;
}) => {
  return (
    <Stack
      alignItems="center"
      gap={{ xs: 0, lg: 0.5 }}
      onClick={onClick}
      sx={{
        minWidth: { xs: 95, lg: 109 },
        ...((disabled || !isActive) && { opacity: '0.5' }),
        ...(!disabled && { '&:hover': { opacity: '1', transition: 'opacity 0.3s ease-in' } }),
      }}
    >
      {icon}
      <DsTypography variant="subtitle2" textAlign="center" sx={{ whiteSpace: 'nowrap' }}>
        {title}
      </DsTypography>
      {subTitle && (
        <DsTypography variant="subtitle2" textAlign="center" sx={{ mt: -1.5 }}>
          {subTitle}
        </DsTypography>
      )}
    </Stack>
  );
};

const ZoneItemWrapper = ({
  children,
  path,
  onClick,
  disabled,
}: { children: React.ReactNode; path?: string; disabled: boolean; onClick?: MouseEventHandler<any> }) => {
  return path ? (
    <Link to={path} onClick={onClick} style={{ ...(!disabled ? { cursor: 'pointer' } : { pointerEvents: 'none' }) }}>
      {children}
    </Link>
  ) : (
    <Box onClick={onClick} component="button" disabled={disabled}>
      {children}
    </Box>
  );
};

const ZoneMenu = () => {
  const location = useLocation();
  const schoolMenuPopover = usePopover();
  const { setCurrentZone } = useCurrentUserStore();

  const {
    schoolZone,
    contentZone,
    communicationZone,
    managementInformationZone,
    workflowZone,
    smartZone,
    satV1Zone,
    satV2Zone,
    satV3Zone,
    satV4Zone,
    satV5Zone,
    timeTableZone,
    challengeZone,
    contactZone,
    commsZone,
    setSchoolZone,
    setContentZone,
    setCommunicationZone,
    setManagementInformationZone,
    setWorkflowZone,
    setSmartZone,
    setSatV1Zone,
    setSatV2Zone,
    setSatV3Zone,
    setSatV4Zone,
    setSatV5Zone,
    setTimeTableZone,
    setChallengeZone,
    setContactZone,
    setCommsZone,
  } = useZoneStore();

  const menuItems = [
    schoolZone,
    contactZone,
    smartZone,
    satV1Zone,
    contentZone,
    commsZone,
    communicationZone,
    workflowZone,
    managementInformationZone,
    timeTableZone,
    challengeZone,
  ];

  useEffect(() => {
    if (schoolZone?.subTitle) {
      setSchoolZone({
        isActive: true,
        icon: <SchoolZoneSmall />,
        onClick: (e) => {
          schoolMenuPopover.onOpen(e);
        },
      });
      setCurrentZone({ name: 'schoolZone' });
    } else if (schoolZone) {
      setSchoolZone({
        isActive: false,
        icon: <SchoolZone />,
        onClick: (e) => {
          schoolMenuPopover.onOpen(e);
        },
      });
    }

    if (schoolMenuPopover?.open) {
      setSchoolZone({
        isActive: true,
      });
    }
  }, [schoolZone?.subTitle, schoolZone?.isVisible, schoolMenuPopover?.open]);

  useEffect(() => {
    const zoneBasePath = getCurrentZoneBasePath(location.pathname);

    if (schoolZone && zoneBasePath === ZONES_BASE_PATH.defaultSchoolZone) {
      setSchoolZone({
        isActive: true,
      });
    } else if (schoolZone) {
      setSchoolZone({
        isActive: false,
        icon: <SchoolZone />,
        subTitle: undefined,
      });
    }

    if (contentZone && zoneBasePath === ZONES_BASE_PATH.content) {
      setContentZone({
        isActive: true,
      });
    } else if (contentZone) {
      setContentZone({
        isActive: false,
      });
    }

    if (communicationZone && zoneBasePath === ZONES_BASE_PATH.communication) {
      setCommunicationZone({
        isActive: true,
      });
    } else if (communicationZone) {
      setCommunicationZone({
        isActive: false,
      });
    }

    if (managementInformationZone && zoneBasePath === ZONES_BASE_PATH.managementInformation) {
      setManagementInformationZone({
        isActive: true,
      });
    } else if (managementInformationZone) {
      setManagementInformationZone({
        isActive: false,
      });
    }

    if (workflowZone && zoneBasePath === ZONES_BASE_PATH.workflow) {
      setWorkflowZone({
        isActive: true,
      });
    } else if (workflowZone) {
      setWorkflowZone({
        isActive: false,
      });
    }

    if (timeTableZone && zoneBasePath === ZONES_BASE_PATH.timeTable) {
      setTimeTableZone({
        isActive: true,
      });
    } else if (timeTableZone) {
      setTimeTableZone({
        isActive: false,
      });
    }

    if (challengeZone && zoneBasePath === ZONES_BASE_PATH.challenge) {
      setChallengeZone({ isActive: true });
    } else if (challengeZone) {
      setChallengeZone({
        isActive: false,
      });
    }

    if (contactZone && zoneBasePath === ZONES_BASE_PATH.contact) {
      setContactZone({
        isActive: true,
      });
    } else if (contactZone) {
      setContactZone({
        isActive: false,
      });
    }

    if (commsZone && zoneBasePath === ZONES_BASE_PATH.comms) {
      setCommsZone({
        isActive: true,
      });
    } else if (commsZone) {
      setCommsZone({
        isActive: false,
      });
    }

    if (smartZone && zoneBasePath === ZONES_BASE_PATH.smart) {
      setSmartZone({
        isActive: true,
      });
    } else if (smartZone) {
      setSmartZone({
        isActive: false,
      });
    }

    if (satV1Zone && zoneBasePath === ZONES_BASE_PATH.satV1) {
      setSatV1Zone({
        isActive: true,
      });
    } else if (satV1Zone) {
      setSatV1Zone({
        isActive: false,
      });
    }

    if (satV2Zone && zoneBasePath === ZONES_BASE_PATH.satV2) {
      setSatV2Zone({
        isActive: true,
      });
    } else if (satV2Zone) {
      setSatV2Zone({
        isActive: false,
      });
    }

    if (satV3Zone && zoneBasePath === ZONES_BASE_PATH.satV3) {
      setSatV3Zone({
        isActive: true,
      });
    } else if (satV3Zone) {
      setSatV3Zone({
        isActive: false,
      });
    }

    if (satV4Zone && zoneBasePath === ZONES_BASE_PATH.satV4) {
      setSatV4Zone({
        isActive: true,
      });
    } else if (satV4Zone) {
      setSatV4Zone({
        isActive: false,
      });
    }

    if (satV5Zone && zoneBasePath === ZONES_BASE_PATH.satV5) {
      setSatV5Zone({
        isActive: true,
      });
    } else if (satV5Zone) {
      setSatV5Zone({
        isActive: false,
      });
    }
  }, [
    location,
    timeTableZone?.isActive,
    challengeZone?.isActive,
    contactZone?.isActive,
    commsZone?.isActive,
    smartZone?.isActive,
    satV1Zone?.isActive,
    satV2Zone?.isActive,
    satV3Zone?.isActive,
    satV4Zone?.isActive,
    satV5Zone?.isActive,
    managementInformationZone?.isActive,
    workflowZone?.isActive,
    communicationZone?.isActive,
    contentZone?.isActive,
  ]);
  return (
    <>
      <Stack
        direction="row"
        justifyContent="center"
        marginRight="auto"
        gap={schoolZone ? 2 : 4}
        data-tour={TIPS.LAYOUT.HEADER.ZONE_MENU.DATA}
      >
        <>
          <ZoneItemWrapper onClick={() => { }} disabled={false} path={'/smart-zone'}>
            <ZoneItem disabled={false} isActive={true} title="Smart Zone" icon={<ContentZone />} />
          </ZoneItemWrapper>
          <ZoneItemWrapper onClick={() => { }} disabled={false} path={'/sat-v1-zone'}>
            <ZoneItem disabled={false} isActive={true} title="SAT V1 Zone" icon={<SchoolZone />} />
          </ZoneItemWrapper>
          <ZoneItemWrapper onClick={() => { }} disabled={false} path={'/sat-v2-zone'}>
            <ZoneItem disabled={false} isActive={true} title="SAT V2 Zone" icon={<SchoolZone />} />
          </ZoneItemWrapper>
          <ZoneItemWrapper onClick={() => { }} disabled={false} path={'/sat-v3-zone'}>
            <ZoneItem disabled={false} isActive={true} title="SAT V3 Zone" icon={<SchoolZone />} />
          </ZoneItemWrapper>
          <ZoneItemWrapper onClick={() => { }} disabled={false} path={'/sat-v4-zone'}>
            <ZoneItem disabled={false} isActive={true} title="SAT V4 Zone" icon={<SchoolZone />} />
          </ZoneItemWrapper>
          <ZoneItemWrapper onClick={() => { }} disabled={false} path={'/sat-v5-zone'}>
            <ZoneItem disabled={false} isActive={true} title="SAT V5 Zone" icon={<SchoolZone />} />
          </ZoneItemWrapper>
          {menuItems.map((item, index) => {
            return item ? (
              <ZoneItemWrapper key={index} onClick={item.onClick} disabled={item.disabled} path={item.path}>
                <ZoneItem
                  disabled={item.disabled}
                  isActive={item.isActive}
                  title={item.title}
                  icon={item.icon}
                  subTitle={item.subTitle}
                />
              </ZoneItemWrapper>
            ) : null;
          })}
        </>
      </Stack>
      <SchoolMenu popover={schoolMenuPopover} />
    </>
  );
};

export default ZoneMenu;
